import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { PageHeader, Card, CardBody } from "../../components/ui";
import {
  FaImage, FaArrowRight, FaArrowLeft, FaUpload, FaLink,
  FaCopy, FaExternalLinkAlt, FaTrashAlt, FaHdd, FaCloud,
  FaChevronRight, FaCheckCircle, FaExclamationTriangle
} from "react-icons/fa";

export default function ImageToUrlGuide() {
  useEffect(() => {
    document.title = "Image & File to URL Guide | Walytic - Upload Files and Get Shareable Links";
  }, []);

  return (
    <div className="page-content">
      <PageHeader title="Image & File to URL Guide" subtitle="Upload images, PDFs, or documents and get a shareable URL in seconds." />

      <div className="max-w-4xl mx-auto">

        {/* Table of Contents */}
        <nav className="bg-surface-card rounded-xl border border-surface-border p-6 mb-12">
          <h2 className="font-semibold text-lg text-ink-primary mb-4">In this guide</h2>
          <div className="grid sm:grid-cols-2 gap-2">
            {[
              { id: 'supported-files', label: 'Supported file types' },
              { id: 'storage-options', label: 'Storage options (R2/S3 or local)' },
              { id: 'upload-and-get-link', label: 'Upload and get your link' },
              { id: 'recent-uploads', label: 'Recent uploads table' },
              { id: 'use-cases', label: 'Where to use generated URLs' },
              { id: 'tips', label: 'Tips' },
            ].map((item) => (
              <a key={item.id} href={`#${item.id}`} className="flex items-center text-sm text-ink-secondary hover:text-brand py-1.5 transition-colors">
                <FaChevronRight className="h-4 w-4 mr-1 text-ink-muted" />
                {item.label}
              </a>
            ))}
          </div>
        </nav>

        {/* Section: Supported Files */}
        <section id="supported-files" className="mb-16 scroll-mt-24">
          <h2 className="text-3xl font-bold text-ink-primary mb-4">Supported file types</h2>
          <p className="text-ink-secondary mb-6 leading-relaxed">
            You can upload most common file formats. The maximum file size is <strong>100 MB</strong>.
          </p>

          <div className="grid sm:grid-cols-2 gap-4">
            <div className="bg-surface-card rounded-xl border border-surface-border p-6">
              <h3 className="font-semibold text-lg text-ink-primary mb-3">Images</h3>
              <div className="flex flex-wrap gap-2">
                {['.jpg', '.png', '.gif', '.webp'].map((ext) => (
                  <code key={ext} className="bg-slate-100 px-2 py-1 rounded text-sm">{ext}</code>
                ))}
              </div>
            </div>
            <div className="bg-surface-card rounded-xl border border-surface-border p-6">
              <h3 className="font-semibold text-lg text-ink-primary mb-3">Documents</h3>
              <div className="flex flex-wrap gap-2">
                {['.pdf', '.zip', '.doc', '.docx'].map((ext) => (
                  <code key={ext} className="bg-slate-100 px-2 py-1 rounded text-sm">{ext}</code>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Section: Storage Options */}
        <section id="storage-options" className="mb-16 scroll-mt-24">
          <h2 className="text-3xl font-bold text-ink-primary mb-4">Storage options</h2>
          <p className="text-ink-secondary mb-6 leading-relaxed">
            Files need to live somewhere accessible via a public URL. Walytic gives you two choices:
          </p>

          <div className="space-y-4">
            <div className="bg-surface-card rounded-xl border border-surface-border p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-8 w-8 rounded-lg bg-sky-100 flex items-center justify-center">
                  <FaCloud className="h-4 w-4 text-sky-600" />
                </div>
                <h3 className="font-semibold text-lg text-ink-primary">Your own bucket (R2 or S3)</h3>
                <span className="text-xs px-2 py-0.5 rounded bg-sky-100 text-sky-700">Recommended</span>
              </div>
              <p className="text-ink-secondary">
                Go to <strong>Settings &gt; Storage</strong> and enter your Cloudflare R2 or AWS S3 credentials.
                Once configured, every file you upload goes straight to your bucket. You own the storage,
                control retention, and the URLs stay active as long as your bucket exists.
              </p>
            </div>

            <div className="bg-surface-card rounded-xl border border-surface-border p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-8 w-8 rounded-lg bg-slate-100 flex items-center justify-center">
                  <FaHdd className="h-4 w-4 text-ink-secondary" />
                </div>
                <h3 className="font-semibold text-lg text-ink-primary">Walytic platform storage</h3>
                <span className="text-xs px-2 py-0.5 rounded bg-slate-100 text-ink-primary">Default</span>
              </div>
              <p className="text-ink-secondary">
                If you have not configured a bucket, files upload to Walytic&apos;s built-in storage.
                This works out of the box with no setup required. Files are served from Walytic&apos;s CDN.
              </p>
            </div>
          </div>
        </section>

        {/* Section: Upload and Get Link */}
        <section id="upload-and-get-link" className="mb-16 scroll-mt-24">
          <h2 className="text-3xl font-bold text-ink-primary mb-4">Upload and get your link</h2>

          <div className="space-y-8">
            <div className="flex gap-4">
              <div className="flex-shrink-0 h-8 w-8 rounded-full bg-orange-600 text-white flex items-center justify-center text-sm font-bold">1</div>
              <div>
                <h3 className="font-semibold text-lg text-ink-primary mb-2">Open the file picker</h3>
                <p className="text-ink-secondary">
                  Go to <strong>Image to URL</strong> in the sidebar. You see a drag-and-drop zone in the center.
                  Drop a file onto it, or click to open the file browser.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 h-8 w-8 rounded-full bg-orange-600 text-white flex items-center justify-center text-sm font-bold">2</div>
              <div>
                <h3 className="font-semibold text-lg text-ink-primary mb-2">Preview your file</h3>
                <p className="text-ink-secondary">
                  For images, a thumbnail preview appears below the upload zone. For documents, you see
                  the filename and size. Confirm it is the right file before uploading.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 h-8 w-8 rounded-full bg-orange-600 text-white flex items-center justify-center text-sm font-bold">3</div>
              <div>
                <h3 className="font-semibold text-lg text-ink-primary mb-2">Click &quot;Upload &amp; Get Link&quot;</h3>
                <p className="text-ink-secondary mb-3">
                  Walytic uploads the file to your configured storage. Once done, a URL appears with two buttons:
                </p>
                <div className="flex gap-3">
                  <div className="flex items-center gap-2 bg-slate-50 border border-surface-border rounded-lg px-4 py-2">
                    <FaCopy className="h-4 w-4 text-ink-secondary" />
                    <span className="text-sm font-medium">Copy</span>
                  </div>
                  <div className="flex items-center gap-2 bg-slate-50 border border-surface-border rounded-lg px-4 py-2">
                    <FaExternalLinkAlt className="h-4 w-4 text-brand" />
                    <span className="text-sm font-medium">Open</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Section: Recent Uploads */}
        <section id="recent-uploads" className="mb-16 scroll-mt-24">
          <h2 className="text-3xl font-bold text-ink-primary mb-4">Recent uploads table</h2>
          <p className="text-ink-secondary mb-6 leading-relaxed">
            Below the upload zone, a table lists all files you have uploaded. Here is what each column shows:
          </p>

          <div className="bg-surface-card rounded-xl border border-surface-border overflow-hidden mb-6">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left py-3 px-4 font-medium text-ink-primary">Column</th>
                  <th className="text-left py-3 px-4 font-medium text-ink-primary">Description</th>
                </tr>
              </thead>
              <tbody className="text-ink-secondary">
                <tr className="border-t border-slate-100">
                  <td className="py-3 px-4 font-medium">Filename</td>
                  <td className="py-3 px-4">Original name of the uploaded file</td>
                </tr>
                <tr className="border-t border-slate-100">
                  <td className="py-3 px-4 font-medium">Size</td>
                  <td className="py-3 px-4">File size in KB or MB</td>
                </tr>
                <tr className="border-t border-slate-100">
                  <td className="py-3 px-4 font-medium">Storage</td>
                  <td className="py-3 px-4">
                    Badge showing <span className="bg-sky-100 text-sky-700 px-1.5 py-0.5 rounded text-xs font-medium">R2</span>,
                    {' '}<span className="bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded text-xs font-medium">S3</span>,
                    {' '}or <span className="bg-slate-100 text-ink-primary px-1.5 py-0.5 rounded text-xs font-medium">Local</span>
                  </td>
                </tr>
                <tr className="border-t border-slate-100">
                  <td className="py-3 px-4 font-medium">Date</td>
                  <td className="py-3 px-4">When the file was uploaded</td>
                </tr>
                <tr className="border-t border-slate-100">
                  <td className="py-3 px-4 font-medium">Actions</td>
                  <td className="py-3 px-4">Open (new tab) and Delete buttons</td>
                </tr>
              </tbody>
            </table>
          </div>

          <p className="text-ink-secondary">
            Use the <strong>&quot;Clear All&quot;</strong> button at the top of the table to delete every uploaded file at once.
            This removes the files from storage permanently.
          </p>
        </section>

        {/* Section: Use Cases */}
        <section id="use-cases" className="mb-16 scroll-mt-24">
          <h2 className="text-3xl font-bold text-ink-primary mb-4">Where to use generated URLs</h2>
          <p className="text-ink-secondary mb-6 leading-relaxed">
            The URLs you generate here plug directly into other Walytic features:
          </p>

          <div className="space-y-4">
            <div className="bg-gradient-to-br from-teal-50 to-emerald-50 border border-teal-200 rounded-xl p-6">
              <h3 className="font-semibold text-teal-900 mb-2">Flow Builder messages</h3>
              <p className="text-teal-800">
                In any Send Message node, paste the URL in the <strong>Media URL</strong> field.
                The image or document attaches to the message automatically.
              </p>
            </div>
            <div className="bg-gradient-to-br from-emerald-50 to-green-50 border border-emerald-200 rounded-xl p-6">
              <h3 className="font-semibold text-emerald-900 mb-2">Send via Excel -- mediaUrl column</h3>
              <p className="text-emerald-800">
                Add the URL to the <code className="bg-white/60 px-1.5 py-0.5 rounded text-sm">mediaUrl</code> column in your
                spreadsheet. Each recipient gets the file attached to their message.
              </p>
            </div>
          </div>
        </section>

        {/* Section: Tips */}
        <section id="tips" className="mb-16 scroll-mt-24">
          <h2 className="text-3xl font-bold text-ink-primary mb-6">Tips</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              { title: 'Use R2/S3 for production', text: 'Platform storage works for testing, but your own bucket gives you full control over file retention and access.' },
              { title: 'Compress images before uploading', text: 'WhatsApp compresses images anyway. Uploading a 10 MB photo wastes bandwidth -- resize to 1200px wide first.' },
              { title: 'Check the URL works', text: 'Click "Open" after uploading to verify the file loads in a browser. If it does not, your bucket permissions may be wrong.' },
              { title: 'Clean up unused files', text: 'Old uploads eat storage space. Delete files you no longer need, or use Clear All after a campaign wraps up.' },
            ].map((tip, i) => (
              <div key={i} className="bg-surface-card border border-surface-border rounded-lg p-4">
                <h4 className="font-semibold text-sm text-ink-primary mb-1">{tip.title}</h4>
                <p className="text-sm text-ink-secondary">{tip.text}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Prev / Next */}
        <div className="flex items-center justify-between border-t border-surface-border pt-8">
          <Link to="/guide/group-export" className="text-sm text-ink-muted hover:text-ink-primary flex items-center gap-1">
            <FaArrowLeft className="h-4 w-4" /> Group Export
          </Link>
          <Link to="/guide/whatsapp-widget" className="text-sm font-medium text-brand hover:opacity-80 flex items-center gap-1">
            Next: WhatsApp Widget <FaArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}
